import _ from "lodash";
import React from "react";
import moment from "moment";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import * as S from "../../../services";
import * as PM from "../../../PurposeModal";
import Form, { FormProps, FormRef } from "../form";
import { useSearchParams } from "react-router-dom";
import { FP, ICONS, T, TB, TC } from "../../../Constants";

//#region Types Shortcuts & Constants
type ElementsData = T.Mapping.MappingAisetData;
type AnyElementsData = T.Mapping.AnyElementsData;
type RegActionMap = T.Options.Reg_action_ext_map;
type Filters = "all" | "mapped" | "unmapped" | "has_documents";
type ExtElements = Awaited<ReturnType<typeof S.loadExtElements>>["data"];
type DocProps = T.DataSource["element_mapping"][number]["items"][number]["documents_props"][number];

const HOUR = 60 * 60 * 1000;

const ELEMENTS_DATA_KEYS: Record<string, keyof ElementsData> = {
    [FP.SITE_FORM]: "sites",
    [FP.BUILDING_FORM]: "buildings",
    [FP.EQUIPEMENT_FORM]: "equipments",
    [FP.EMPLACEMENT_FORM]: "emplacements",
}

/** Some components don't render well with the label on the right */
const NO_CUSTOM_LABEL = {
    [FP.SITE_FORM]: [] as (keyof T.SiteData)[],
    [FP.BUILDING_FORM]: [] as (keyof T.BuildingData)[],
    [FP.EMPLACEMENT_FORM]: [] as (keyof T.EmplacementData)[],
    [FP.EQUIPEMENT_FORM]: ["category"] as (keyof T.EquipmentData)[],
}

/** Sticky footer CSS properties */
const StickyFooter: React.CSSProperties = { position: 'fixed', bottom: 0, right: 0, padding: '0 1rem 1rem 0', zIndex: 1000 };

/** Which fields should always be rendered in the form for each form type */
const ALWAYS_RENDER_FIELDS = {
    [FP.SITE_FORM]: [] as (keyof T.SiteData)[],
    [FP.BUILDING_FORM]: [] as (keyof T.BuildingData)[],
    [FP.EMPLACEMENT_FORM]: [] as (keyof T.EmplacementData)[],
    [FP.EQUIPEMENT_FORM]: ["category"] as (keyof T.EquipmentData)[],
}

/** Custom filter options */
const FilterOptions: T.Option<object, Filters>[] = [
    { label: TC.DATASOURCE_MAPPER_FILTER_ALL, value: "all" },
    { label: TC.DATASOURCE_MAPPER_FILTER_MAPPED, value: "mapped" },
    { label: TC.DATASOURCE_MAPPER_FILTER_UNMAPPED, value: "unmapped" },
    { label: TC.DATASOURCE_MAPPER_FILTER_HAS_DOCS, value: "has_documents" },
]
//#endregion

export type MapperProps = {
    /** The datasource */
    datasource: T.DataSource;
    /** The context's elements */
    elements: ExtElements["elements"];
    /** Show the component in a modal */
    popup?: boolean;
    /** Modal parameters */
    modal?: M.StyleModalProps;
    /** Callback when an element was added */
    add_element: (elem_id: string, site_id: string) => Promise<ExtElements["elements"][number]["context"][number]>;
    /** Callback to close the modal */
    close?: () => void;
    /** Callback to validate the mapping */
    validate: (datasource: T.DataSource) => void;
}

// TODO: on opening, try again to match the reg action ?

export const Mapper: React.FC<MapperProps> = ({ validate, add_element, ...props }) => {
    const lg = H.useLanguage();
    const [forms] = H.useFormIds();
    const saving = H.useBoolean(false);
    const [filter, set_filter] = React.useState<Filters>("all");
    const [active_appliance, set_active_appliance] = React.useState("");
    const [datasource, set_datasource] = React.useState(props.datasource);
    const [context_elements, set_context_elements] = React.useState(props.elements);
    const [active_site, set_active_site] = React.useState(props.datasource.element_mapping?.[0]?.site_id || "");
    const [elements_data, set_elements_data] = H.useAsyncState<ElementsData>({ sites: [], buildings: [], emplacements: [], equipments: [] });
    /** The current list of mapped reg actions */
    const [reg_action_map, set_reg_action_map, reg_action_map_status] = H.useAsyncState<RegActionMap[]>([]);
    /** The reg actions available in the app */
    const [reg_actions, set_reg_actions, reg_actions_status] = H.useAsyncState<T.API.Reg.RegActionResume[]>([]);
    /** Store the datasource in a ref, to allow to load reg actions that are not technically in the current context */
    const datasource_ref = React.useRef(props.datasource);
    React.useImperativeHandle(datasource_ref, () => datasource);

    React.useEffect(() => set_context_elements(props.elements), [props.elements]);

    React.useEffect(() => {
        let isSubscribed = true;
        const element_mapping = datasource_ref.current.element_mapping || [];
        const actions_ids = element_mapping.map(em => em.items.map(i => i.documents_props.map(d => d.regAction))).flat(2);
        const sites = element_mapping.map(em => em.site_id);
        S.contextActions({ roots: sites, onlyCheckRegions: true, selectedActions: actions_ids })
            .then(res => isSubscribed && set_reg_actions(res.data, "done"))
            .catch(() => isSubscribed && set_reg_actions([], "error"));
        return () => {
            isSubscribed = false;
            set_reg_actions([], "load");
        }
    }, [active_site, set_reg_actions]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getEquipmentResource({ type: "reg_action_ext_map" })
            .then(res => isSubscribed && set_reg_action_map(res.data as RegActionMap[], "done"))
            .catch(() => isSubscribed && set_reg_action_map([], "error"));
        return () => {
            isSubscribed = false;
            set_reg_action_map([], "load");
        }
    }, [set_reg_action_map]);

    const save_datasource = React.useCallback(() => {
        saving.setTrue();
        // Save the datasource
        S.saveDataSource(datasource).then(results => {
            if (typeof validate === "function") validate(results.data);
            else set_datasource(results.data);
            M.Alerts.success_alert({ message: TC.DATASOURCE_MAPPER_COMPLETE_SAVE });
        })
            .catch(M.Alerts.updateError)
            .finally(saving.setFalse)
    }, [datasource, validate, saving]);

    //#region Miscellanous
    const current_element = React.useMemo(() => {
        const element = context_elements.find(e => e.site_id === active_site);
        let appliances = datasource.element_mapping.find(e => e.site_id === active_site)?.items || [];

        if (element) {
            if (filter === "mapped") appliances = appliances.filter(a => a.app);
            else if (filter === "unmapped") appliances = appliances.filter(a => !a.app);
            else if (filter === "has_documents") appliances = appliances.filter(a => (a.documents_props || []).length > 0);
            return { ...element, appliances };
        }
        else return { appliances: [], context: [], site_id: "", site_name: "", documents: [] };
    }, [active_site, context_elements, datasource.element_mapping, filter]);

    const statuses = React.useMemo(() => [reg_action_map_status, reg_actions_status], [reg_action_map_status, reg_actions_status]);
    const reg_actions_options = React.useMemo(() => reg_actions.map(a => ({ ...a, label: a.name, prop: "name", value: a._id })), [reg_actions]);

    const footer = React.useMemo(() => <C.Button
        onClick={save_datasource}
        text={TC.DATASOURCE_MAPPER_CONFIRM}
        icon={{ spin: saving.value, icon: "save", spinIcon: "spinner" }}
    />, [save_datasource, saving.value]);
    //#endregion

    //#region Changes & Events
    const load_elem_properties = React.useCallback((item_id: string, reload = false, update_option = false, option?: MapperProps["elements"][number]["context"][number]) => new Promise<void>(resolve => {
        let aiset_elem_promise: Promise<void>;
        // Retrieve the selected option
        const selected_option = option || current_element.context.find(o => o.value === item_id);
        // Check if the data was already loaded
        const is_loaded = (elements_data[ELEMENTS_DATA_KEYS[selected_option?.type]] || [])
            .some((e: AnyElementsData) => e._id === selected_option.value || e.id === selected_option.value);

        if (!is_loaded || reload) {
            if ([FP.SITE_FORM, FP.BUILDING_FORM].includes(selected_option?.type)) aiset_elem_promise = new Promise((resolve, reject) => {
                const is_site = selected_option?.type === FP.SITE_FORM;
                const api: T.API.ToAxios<T.API.Utils.Tables.GetSitesRows | T.API.Utils.Tables.GetBuildingsRows> = is_site
                    ? S.getSitesRows
                    : S.getBuildingsRows;

                api(selected_option.value).then(results => {
                    const property: keyof Pick<ElementsData, "buildings" | "sites"> = is_site ? "sites" : "buildings";
                    const rows = results.data as ElementsData[typeof property];

                    // Update the context options
                    if (update_option) set_context_elements(p => p.map(element => {
                        if (element.site_id === active_site) return {
                            ...element,
                            context: element.context.map(o => {
                                const updated_item = rows.find(e => e.id === o.value);
                                if (updated_item) return { ...o, label: updated_item.name };
                                else return o;
                            }),
                        }
                        else return element;
                    }));

                    // Update the elements data
                    set_elements_data(p => {
                        const existing_ids = p[property].map((e: ElementsData[typeof property][number]) => e.id);
                        const [new_items, existing_items] = _.partition(rows, (e: typeof rows[number]) => !existing_ids.includes(e.id));
                        // Create a new set of equipments
                        const items_new_set = p[property]
                            .map((e: typeof rows[number]) => existing_items.find(x => x.id === e.id) || e)
                            .concat(new_items);
                        return { ...p, [property]: items_new_set };
                    });
                })
                    .catch(reject)
                    .finally(resolve);
            })
            else aiset_elem_promise = new Promise((resolve, reject) => {
                const is_equipment = selected_option?.type === FP.EQUIPEMENT_FORM;
                const property: keyof Pick<ElementsData, "emplacements" | "equipments"> = is_equipment ? "equipments" : "emplacements";
                const api: T.API.ToAxios<T.API.Utils.Tables.GetEmplacementRowsByIds | T.API.Utils.Tables.GetEquipmentsRowsByIds> = is_equipment
                    ? S.getEquipmentRowsByIds
                    : S.getEmplacementRowsByIds;


                api(selected_option.value).then(res => {
                    const rows = res.data as ElementsData[typeof property];
                    // Update the context options
                    if (update_option) set_context_elements(p => p.map(element => {
                        if (element.site_id === active_site) return {
                            ...element,
                            context: element.context.map(o => {
                                const updated_item = res.data.find(e => e._id === o.value)
                                if (updated_item) return { ...o, label: updated_item.name }
                                else return o;
                            })
                        }
                        else return element;
                    }));
                    // Update the elements data
                    set_elements_data(p => {
                        const existing_ids = p[property].map((e: ElementsData[typeof property][number]) => e._id);
                        const [new_items, existing_items] = _.partition(rows, (e: typeof rows[number]) => !existing_ids.includes(e._id));
                        // Create a new set of equipments
                        const items_new_set = p[property]
                            .map((e: typeof rows[number]) => existing_items.find(x => x._id === e._id) || e)
                            .concat(new_items);
                        return { ...p, [property]: items_new_set };
                    });
                })
                    .catch(reject)
                    .finally(resolve);
            });
        }

        if (aiset_elem_promise) {
            const dismount = M.renderLoader();
            aiset_elem_promise
                .then(() => resolve())
                .catch(M.Alerts.loadError)
                .finally(dismount);
        }
        else resolve();
    }), [current_element.context, elements_data, active_site, set_elements_data]);

    const switch_active = React.useMemo(() => ({
        site: (site_id: string) => {
            set_active_appliance("");
            set_active_site(site_id);
        },
        appliance: (appliance_id?: string) => {
            const app_equivalent = current_element.appliances.find(a => a.external === appliance_id)?.app;
            if (app_equivalent) load_elem_properties(app_equivalent).then(() => set_active_appliance(appliance_id || ""));
            else set_active_appliance(appliance_id || "");
        }
    }), [current_element.appliances, load_elem_properties]);

    const change = React.useMemo(() => ({
        equivalent: (id: string, equivalent: string, reload = false, update_option = false, option?: MapperProps["elements"][number]["context"][number]) => {
            // The callback to update the datasource
            const set_equivalent = () => set_datasource(p => ({
                ...p,
                element_mapping: p.element_mapping.map(e => {
                    if (e.site_id !== active_site) return e;
                    else return { ...e, items: e.items.map(i => i.external === id ? { ...i, app: equivalent } : i) }
                }),
            }));

            if (equivalent) load_elem_properties(equivalent, reload, update_option, option).then(set_equivalent);
            else set_equivalent();
        },
        element: (id: string, equivalent: string, update_option = false) => {
            // Close the accordion
            set_active_appliance("");
            // Reload the element data
            change.equivalent(id, equivalent, true, update_option);
        },
        add_element: (text: string, external_id: string) => {
            const parent_params = {
                isRequired: true,
                filter_by: "path",
                add_all_parents: true,
                no_error_if_empty: true,
                context: { roots: active_site },
                filter_value: FP.EMPLACEMENT_FORM,
                label: TC.MAPPER_ADD_ITEM_PARENT_TITLE,
            } as Parameters<typeof PM.renderContextElements>[0];

            PM.renderContextElements(parent_params).then(origin => {
                if (origin) M.renderFormModal<T.EquipmentData>({ path: FP.EQUIPEMENT_FORM, forcedSubmission: [{ prop: "name", value: text }] }).then(equipment => {
                    if (equipment) {
                        const dismount = M.renderLoader();
                        S.attachNode({ children: equipment._id, parent: origin })
                            .then(() => add_element(equipment._id, active_site)
                                .then(option => change.equivalent(external_id, equipment._id, false, false, option))
                                .catch(M.Alerts.updateError)
                                .finally(dismount))
                            .catch(error => {
                                M.Alerts.updateError(error);
                                dismount();
                            });
                    }
                });
            });
        },
        ignore_doc: (appliance_id: string, document_ext_id: string) => set_datasource(p => ({
            ...p,
            element_mapping: p.element_mapping.map(map => {
                if (map.site_id !== active_site) return map;
                else return {
                    ...map,
                    items: map.items.map(item => {
                        if (item.external !== appliance_id) return item;
                        else return {
                            ...item,
                            documents_props: item.documents_props.filter(doc => doc.ext_id !== document_ext_id),
                            documents: {
                                treated: item.documents.treated.concat(document_ext_id),
                                untreated: item.documents.untreated.filter(d => d !== document_ext_id),
                            }
                        }
                    }),
                }
            }),
        })),
        reg_map: (document: DocProps, reg_action: string) => {
            // Retrieve a mapped reg action and reg status if it exists
            const existing_map = reg_action_map.find(m => m.value === document.ext_reg);
            const existing_status = existing_map?.statuses?.[document.ext_conclusion];

            // Update every document that has the same reg action
            const update_element = () => set_datasource(p => ({
                ...p,
                element_mapping: p.element_mapping.map(map => ({
                    ...map,
                    items: map.items.map(item => ({
                        ...item,
                        documents_props: item.documents_props.map(doc => {
                            if (doc.ext_reg !== document.ext_reg) return doc;
                            else return { ...doc, regAction: reg_action, ext_map_status: existing_status };
                        })
                    }))
                }))
            }));

            // Just apply the map if it exists
            if (existing_map) update_element();
            else if (reg_action) M.askConfirm({ title: TC.UPDATE_REG_MAP_DATASOURCE_TITLE, text: TC.UPDATE_REG_MAP_DATASOURCE_MESSAGE }).then(confirmed => {
                if (confirmed) {
                    const extra_data: Omit<RegActionMap, "label" | "value"> = { app_reg_id: reg_action, statuses: {} };
                    const new_option: RegActionMap = { label: document.ext_reg, value: document.ext_reg, ...extra_data };
                    // Create a new map
                    S.createOption([{ option_code: "reg_action_ext_map", label: new_option.label, value: new_option.value, extra: JSON.stringify(extra_data) }]).then(() => {
                        // Add the new map to the list
                        set_reg_action_map(p => p.concat(new_option))
                        // Select the new map
                        update_element();
                    }).catch(M.Alerts.updateError);
                }
            });
        },
        add_reg_action: (text: string, document: DocProps) => {

            const base_submission: Parameters<typeof M.renderPopUpFormModal<T.RegAction>>[0]["forcedSubmission"] = [
                { prop: "name" as keyof T.RegAction, value: text as T.RegAction["name"] },
                { prop: "type" as keyof T.RegAction, value: "reg" as T.RegAction["type"], disable: true },
                { prop: "auto_file" as keyof T.RegAction, value: false as T.RegAction["auto_file"] },
            ];

            M.renderFormModal<T.RegAction>({ path: FP.ACTION_REG_FORM, forcedSubmission: base_submission }).then(reg_action => {
                if (reg_action) {
                    set_reg_actions(p => p.concat({
                        regions: [],
                        conform: false,
                        conformity: {},
                        _id: reg_action._id,
                        name: reg_action.data.name,
                        noFreq: reg_action.data.noFreq,
                        countries: reg_action.data.countries,
                        frequency: reg_action.data.frequency,
                        resources: reg_action.data.resources,
                    }));
                    change.reg_map(document, reg_action._id);
                }
            });
        }
    }), [active_site, context_elements, elements_data, reg_action_map, load_elem_properties, add_element, set_reg_action_map, set_reg_actions]);

    const fetch_files = React.useCallback((doc: DocProps) => {
        return new Promise<T.File[]>((resolve, reject) => {
            const unmount = M.renderLoader();
            /* TODO: Probably not this API to use */
            S.getBTVDocFiles({ credentials: props.datasource.credentials, document_id: doc.ext_file })
                .then(res => resolve(res.data))
                .catch(reject)
                .finally(unmount)
        });
    }, [props.datasource.credentials]);

    const save_reg = React.useCallback((appliance_id: string, document: DocProps, reg_doc: T.RegDoc) => {
        // Update the mapping of the conclusion
        const existing_map = reg_action_map.find(m => m.value === document.ext_reg);
        const doc_status = reg_doc.currentElement?.[0]?.status;

        const update = () => {
            set_datasource(p => ({
                ...p,
                element_mapping: p.element_mapping.map(map => ({
                    ...map,
                    items: map.items.map(item => {
                        if (item.external !== appliance_id) return item;
                        else return {
                            ...item,
                            documents_props: item.documents_props.filter(doc => doc.ext_id !== document.ext_id),
                            documents: {
                                treated: item.documents.treated.concat(document.ext_id),
                                untreated: item.documents.untreated.filter(d => d !== document.ext_id),
                            }
                        }
                    })
                }))
            }));

            const reg_action_name = reg_actions.find(a => a._id === reg_doc.regAction)?.name;
            set_context_elements(p => p.map(context => ({
                ...context,
                documents: context.documents.concat({
                    action_id: reg_doc.regAction,
                    action_name: reg_action_name,
                    doc_date: reg_doc.last_control,
                    elements_ids: reg_doc.currentElement.map(e => e.elem),
                    files: reg_doc.files.map(f => ({ url: f.url, name: f.name })),
                })
            })))
        };

        // The status of the document was already mapped
        if (existing_map?.statuses?.[document?.ext_conclusion]) update();
        // Add the status to the mapping
        else if (existing_map && doc_status) {
            const extra_data: Omit<RegActionMap, "label" | "value"> = { app_reg_id: existing_map.app_reg_id, statuses: { ...existing_map.statuses, [document.ext_conclusion]: doc_status } };
            S.updateOption({ option_code: "reg_action_ext_map", prop: "extra", new_value: JSON.stringify(extra_data), value: existing_map.value })
                .then(() => set_reg_action_map(p => p.map(o => o.value === existing_map.value ? { ...o, ...extra_data } : o)))
                .catch(console.error)
                .finally(() => update());
        }
        else update();
    }, [active_site, reg_action_map, reg_actions, set_reg_action_map]);
    //#endregion

    //#region Rendering
    const render_context_item = React.useCallback((item: MapperProps["elements"][number]["context"][number]) => <div>
        <i className={`fa fa-${ICONS.EMPLACEMENT_ICONS[item.emplacement] || ICONS.FA_ICONS[item.type]}`} />
        <span className="mx-2">{item.label}</span>
        {item.full_path && <span className="text-muted">({item.full_path})</span>}
    </div>, []);

    const render_reg_action_item = React.useCallback((option: typeof reg_actions_options[number]) => {
        return <BS.Row key={option.value}>
            <BS.Col md={7} className="text-break" style={{ whiteSpace: "break-spaces" }}>{option.label}</BS.Col>
            <BS.Col md={4} className="text-break text-end" style={{ whiteSpace: "break-spaces" }}>
                {option.regions.length > 0 ? option.regions.map(r => r.name).join() : option.countries.join()}
            </BS.Col>
            <BS.Col md={1}>{option.noFreq ? "∞" : option.frequency || "X"}</BS.Col>
        </BS.Row>
    }, []);

    const render_properties = React.useCallback((appliance: typeof current_element.appliances[number]) => {
        const form_id = forms[appliance.path];
        // Retrieve the selected option
        const selected_option = current_element.context.find(o => o.value === appliance.app);
        const aiset_item = (elements_data[ELEMENTS_DATA_KEYS[selected_option?.type]] || [])
            .find((e: AnyElementsData) => e._id === selected_option?.value || e.id === selected_option?.value) as AnyElementsData;

        const form_ref: React.MutableRefObject<FormRef> = { current: null };
        const to_render_fields: string[] = appliance.data.map(p => p.app_name as string)
            .concat(ALWAYS_RENDER_FIELDS[appliance.path] as string[] || [])
            .concat(Object.keys(aiset_item || {}));

        // Render the field with a custom output
        const render_field: FormProps<Record<string, unknown>>["render_field"] = (key, Component, input_props) => {
            let entry_match = appliance.data.find(p => p.app_name === key);
            const no_custom_label = (NO_CUSTOM_LABEL[appliance.path] || []).includes(key);

            // Special case to render the category field anyway
            if (!entry_match && appliance.path === FP.EQUIPEMENT_FORM && key === "category") {
                entry_match = { app_name: "category", data_flag: "single", options: [], value: "" };
            }

            // The property is not found in the external API
            if (!entry_match) return null;
            // The property is found in the external API
            const value = entry_match?.value as string || "";
            const property = key as keyof AnyElementsData;
            // Map a fixed value with it's aiset equivalent, to compare with the value from the external API
            const property_value = aiset_item
                ? TB.Datasources.map_value(aiset_item, property, selected_option?.type || "")
                : "";
            // Check if the values are the same
            const is_same_value = aiset_item && TB.areStringSimilar(TB.getString(property_value), value, { caseSensitive: false, inclusion: "both", precision: 0.9 });
            // Render the tip
            const tip_props: C.IconTipProps = is_same_value
                ? { icon: "check-circle", className: "me-2 text-success", tipContent: TC.DATASOURCE_DB_MATCH }
                : { icon: "exclamation-circle", className: "me-2 text-danger", tipContent: TC.DATASOURCE_DB_NO_MATCH };

            // Store a ref to interact with the input component
            const component_ref: React.MutableRefObject<C.Form.InputRef> = { current: null };

            const change_value = () => {
                // Get the correct function to apply the value
                let apply: typeof component_ref.current.apply_string;
                // Apply function vary from the input type
                if (input_props?.type === "number") apply = (component_ref.current as C.Form.NumFieldRef)?.single?.current?.apply_string;
                else if (input_props?.type === "textfield" || input_props?.type === "textarea" || input_props?.type === "email") {
                    apply = (component_ref.current as C.Form.TextFieldRef)?.single?.current?.apply_string;
                }
                else apply = component_ref.current?.apply_string;

                // Apply the value through the input component
                if (typeof apply === "function") apply(value);
                // Announce that it's not possible to change the value of this input type
                else M.renderAlert({ type: "warning", message: TC.DATASOURCE_INPUT_TYPE_NOT_SUPPORTED });
            }

            // Render the row
            return <BS.Row className="g-1 mb-2 align-items-center" key={key}>
                {entry_match.options.length > 1
                    ? <BS.Col xs={5}>
                        {/* TODO: Allow to change suggested value in here */}
                        <C.Form.Select
                            value={value}
                            onChange={console.log}
                            options={(entry_match.options as string[]).map((o, index) => ({ label: o, value: index }))}
                        />
                    </BS.Col>
                    : <BS.Col xs={5}>
                        {aiset_item && <C.IconTip {...tip_props} />}
                        <span className="fw-bold">{lg.getTextObj(form_id, key, key)}:</span> {value || ""}
                    </BS.Col>}

                {aiset_item && <>
                    <BS.Col xs={1} className="text-center">
                        <C.Button
                            size="sm"
                            className="ms-2"
                            icon="caret-right"
                            onClick={change_value}
                            variant={is_same_value ? "secondary" : "primary"}
                            disabled={is_same_value || input_props.hidden || input_props.disabled}
                        />
                    </BS.Col>

                    <BS.Col className="d-flex align-items-center" xs={6}>
                        {Component && <Component
                            {...input_props}
                            noBottomMargin
                            ref={component_ref}
                            customClass="flex-grow-1"
                            hideLabel={no_custom_label}
                            render_label={params => params.src_icon}
                            labelPosition={no_custom_label ? "top" : "right"}
                        />}
                    </BS.Col>
                </>}
            </BS.Row>;
        };

        // Create a form to render the editable aiset item
        if (aiset_item) return <>
            {appliance.path === FP.EQUIPEMENT_FORM && <C.Flex alignItems="center" justifyContent="end" className="mb-2 fs-85 text-muted">
                {lg.getStaticText(TC.DATASOURCE_MAPPER_EQUIP_CATEGORY_WARNING)}
            </C.Flex>}

            <Form<Record<string, unknown>>
                ref={form_ref}
                save_size="sm"
                partial="in-app"
                inactivity_lock={HOUR}
                key={appliance.external}
                render_field={render_field}
                path={selected_option?.type}
                partial_fields={to_render_fields}
                submissionId={selected_option.value}
                save_label={TC.DATASOURCE_MAPPER_FORM_SAVE_BUTTON}
                onSave={sub => change.element(appliance.external, appliance.app, sub?.data?.name !== aiset_item.name)}
            />
        </>;
        // Render each property but without creating a form
        else return appliance.data.map(entry => render_field(entry.app_name, null, null));
    }, [lg, forms, elements_data, current_element, change, active_appliance, active_appliance]);

    const render_documents = React.useCallback((appliance: typeof current_element.appliances[number]) => {
        let item_documents = [] as typeof current_element.documents[number][];
        if (appliance.app) item_documents = current_element.documents.filter(d => d.elements_ids.includes(appliance.app) && TB.mongoIdValidator(d.action_id));

        const create_doc = (doc: typeof appliance.documents_props[number]) => {
            M.renderRegDoc({
                forceNewDoc: true,
                title: doc.ext_reg,
                roots: active_site,
                action: doc.regAction,
                elemId: appliance.app,
                default_status: doc.ext_map_status,
                fetch_files: () => fetch_files(doc),
                default_doc: { controller: doc.controller, last_control: doc.last_control, notes: doc.notes, user: doc.user }
            })
                .then(reg_doc => {
                    if (reg_doc?.action === "saved") save_reg(appliance.external, doc, reg_doc.doc);
                });
        }

        return <div>
            <C.Title level={5} text={TC.DATASOURCE_DOCUMENTS} />

            {item_documents.length > 0 && <BS.Accordion className="mb-3">
                <BS.Accordion.Item eventKey={appliance.external + "_documents"}>
                    <BS.Accordion.Header>
                        {lg.getStaticText(TC.DATASOURCE_EXISTING_DOCUMENTS)}
                    </BS.Accordion.Header>
                    <BS.Accordion.Body>
                        {item_documents.map(doc => <C.Flex key={doc.action_id} justifyContent="between" alignItems="center">
                            <div>
                                <span className="me-2">{lg.getTextObj(doc.action_id, "name", doc.action_name)}</span>
                                <span className="text-muted">{moment(doc.doc_date).format("DD/MM/YYYY")}</span>
                            </div>
                            <div>
                                {doc.files.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noopener noreferrer"><C.IconTip icon="file" tipContent={f.name} /></a>)}
                            </div>
                        </C.Flex>)}
                    </BS.Accordion.Body>
                </BS.Accordion.Item>
            </BS.Accordion>}

            <BS.Row className="g-2">
                {appliance.documents_props.map(doc => <BS.Col md={4} key={doc.ext_id}>
                    <BS.Card className="h-100">
                        <BS.Card.Body className="pb-1">
                            <div className="mb-2">
                                <C.Flex className="fw-bold" justifyContent="between" alignItems="center">
                                    {doc.ext_reg}
                                    <C.IconTip
                                        icon="file"
                                        className="pointer text-info"
                                        onClick={() => fetch_files(doc).then(r => {
                                            const file = r[0];
                                            if (file) {
                                                const a = document.createElement('a');
                                                a.href = file.url;
                                                a.download = file.originalName;
                                                a.rel = "noopener noreferrer";
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                            }
                                        })}
                                    />
                                </C.Flex>
                                <div className="text-muted">{doc.ext_conclusion && ` (Conclusion: ${doc.ext_conclusion})`}</div>
                                <div className="text-muted fs-85">{doc.ext_id}</div>
                                <div className="text-muted fs-85">{doc.ext_close_date && moment(doc.ext_close_date).format("DD/MM/YYYY")}</div>
                                <div className="text-muted fs-85">{doc.ext_desc && doc.ext_desc}</div>
                            </div>

                            <C.Form.Select
                                addResource
                                noBottomMargin
                                value={doc.regAction}
                                options={reg_actions_options}
                                label={TC.DATASOURCE_DOC_REG_ACTION_MAP}
                                disabled={TB.mongoIdValidator(doc.regAction)}
                                onChange={reg_action => change.reg_map(doc, reg_action)}
                                typeahead={{
                                    dropdownFit: true,
                                    renderItem: render_reg_action_item,
                                    onAddOption: text => change.add_reg_action(text, doc)
                                }}
                                suffix_button={{
                                    size: "sm",
                                    icon: "forward",
                                    variant: "warning",
                                    tip: TC.DATASOURCE_IGNORE_DOC,
                                    onClick: () => change.ignore_doc(appliance.external, doc.ext_id),
                                }}
                            />
                        </BS.Card.Body>

                        <BS.Card.Footer className="pt-1">
                            <C.Flex justifyContent="end">
                                <C.Button
                                    size="sm"
                                    variant="excel"
                                    icon="file-import"
                                    text={TC.DATASOURCE_CREATE_DOC}
                                    onClick={() => create_doc(doc)}
                                />
                            </C.Flex>
                        </BS.Card.Footer>
                    </BS.Card>
                </BS.Col>)}
            </BS.Row>
        </div>;
    }, [change, reg_actions_options, current_element, lg, active_site, fetch_files, save_reg, render_reg_action_item]);
    //#endregion

    return React.createElement(
        props.popup ? M.BlankModal : React.Fragment,
        props.popup ? {
            ...props.modal,
            footer: footer,
            onQuit: props.close,
            size: props.modal?.size || "xl",
            title: props.modal?.title || TC.DATASOURCE_ELEMENT_MAPPING,
        } as M.BlankModalProps : null,
        <>

            <C.Flex justifyContent="between" alignItems="center">
                <C.Title className="mb-3" text={TC.DATASOURCE_ELEMENT_MAPPING} />

                <C.Form.Select
                    no_clear_btn
                    value={filter}
                    noBottomMargin
                    onChange={set_filter}
                    options={FilterOptions}
                    label={TC.DATASOURCE_MAPPER_FILTER}
                />
            </C.Flex>

            <C.Spinner min_load_size="500px" status={statuses}>

                {/* Tab Panels */}
                {context_elements.length > 1 && <C.Flex className="mb-3">
                    {context_elements.map((elem, index) => <C.Button
                        size="sm"
                        variant="info"
                        key={elem.site_id}
                        text={elem.site_name}
                        active={active_site === elem.site_id}
                        onClick={() => switch_active.site(elem.site_id)}
                        className={index === context_elements.length - 1 ? "" : "me-2"}
                    />)}
                </C.Flex>}

                <BS.Row className="g-1 mb-2 fs-110 fw-bold">
                    <BS.Col xs={6} children={lg.getStaticText(TC.DATASOURCE_API_LABEL)} />
                    <BS.Col xs={6} children={lg.getStaticText(TC.DATASOURCE_AISET_LABEL)} />
                </BS.Row>

                <BS.Accordion className="mb-3" activeKey={active_appliance} onSelect={key => switch_active.appliance(key as string)}>
                    {current_element.appliances.map(appliance => <BS.Accordion.Item key={appliance.external} eventKey={appliance.external}>
                        <BS.Accordion.Header onKeyUp={e => e.code === "Space" && e.preventDefault()}>
                            <BS.Row className="g-1 w-100">
                                <BS.Col xs={6}>
                                    <i className={`me-2 fa fa-${ICONS.FA_ICONS[appliance.path]}`} />
                                    {appliance.data.find(d => d.app_name === "name")?.value || "N/A"}
                                </BS.Col>

                                {/* Stop propagation to not open the accordion when clicking the select */}
                                <BS.Col xs={6} onClick={e => e.stopPropagation()}>
                                    <C.Form.Select
                                        noBottomMargin
                                        customClass="me-2"
                                        value={appliance.app || ""}
                                        addResource={appliance.path === FP.EQUIPEMENT_FORM}
                                        onChange={value => change.equivalent(appliance.external, value)}
                                        options={current_element.context.filter(o => o.type === appliance.path)}
                                        typeahead={{
                                            renderItem: render_context_item,
                                            onAddOption: text => change.add_element(text, appliance.external),
                                        }}
                                    />
                                </BS.Col>
                            </BS.Row>
                        </BS.Accordion.Header>

                        <BS.Accordion.Body>
                            {render_properties(appliance)}
                            {render_documents(appliance)}
                        </BS.Accordion.Body>
                    </BS.Accordion.Item>)}
                </BS.Accordion>

                {!props.popup && <C.Flex
                    children={footer}
                    alignItems="center"
                    justifyContent="end"
                    style={StickyFooter}
                />}

            </C.Spinner>
        </>
    );
}

export const MapperContext: React.FC = () => {
    const [params] = useSearchParams();
    const datasource_id = React.useMemo(() => params.get("id"), [params]);
    const [data, set_data, status] = H.useAsyncState<ExtElements>({ elements: [], datasource: null });

    React.useEffect(() => {
        let isSubscribed = true;
        if (!TB.mongoIdValidator(datasource_id)) set_data({ elements: [], datasource: null }, "error");
        else S.loadExtElements(datasource_id)
            .then(res => isSubscribed && set_data(res.data, "done"))
            .catch(() => isSubscribed && set_data({ elements: [], datasource: null }, "error"));
        return () => {
            isSubscribed = false;
            set_data({ elements: [], datasource: null }, "load");
        }
    }, [set_data, datasource_id]);

    const add_element = React.useCallback<MapperProps["add_element"]>((elem_id, site_id) => new Promise((resolve, reject) => {
        // Timeout to give time to arango to process the new element //TODO: Remove once arango is the main database
        setTimeout(() => S.getContextOptions({ context: { roots: elem_id }, include_context: true, add_all_parents: true }).then(res => {
            const options = res.data.filter(o => o.value === elem_id);

            set_data(p => ({
                ...p,
                elements: p.elements.map(elem => {
                    if (elem.site_id !== site_id) return elem;
                    else return { ...elem, context: elem.context.concat(options) }
                }),
            }));
            resolve(options[0]);
        }).catch(reject), 2500);
    }), [set_data]);

    return <div className="flex-grow-1 temp test">
        <C.Spinner status={status}>
            <Mapper
                validate={console.log}
                elements={data.elements}
                add_element={add_element}
                datasource={data.datasource}
            />
        </C.Spinner>
    </div>
}